import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiBookOpen, FiUser, FiCalendar, FiUsers, FiSearch, FiShoppingCart, FiInfo, FiPlus, FiMinus, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import logger from '../../utils/logger';
import styles from './CourseCatalog.module.css';

function CourseCatalog() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  
  // Check if user came from login/signup with redirect=/catalog
  const searchParams = new URLSearchParams(location.search);
  const redirectParam = searchParams.get('redirect');
  const redirectFromStorage = typeof window !== 'undefined' 
    ? localStorage.getItem('faithinsight_login_redirect') 
    : null;
  
  // Show message if redirect parameter is /catalog in URL or was stored in localStorage
  const showRedirectMessage = redirectParam === '/catalog' || redirectFromStorage === '/catalog';
  
  // Clear the redirect from localStorage and URL after showing the message
  useEffect(() => {
    if (showRedirectMessage) {
      // Clear from localStorage
      if (redirectFromStorage === '/catalog') {
        localStorage.removeItem('faithinsight_login_redirect');
      }
      
      // Clear from URL query parameters (replace state to remove ?redirect=/catalog from URL)
      if (redirectParam === '/catalog') {
        const newSearchParams = new URLSearchParams(location.search);
        newSearchParams.delete('redirect');
        const newSearch = newSearchParams.toString();
        const newUrl = newSearch ? `${location.pathname}?${newSearch}` : location.pathname;
        window.history.replaceState({}, '', newUrl);
      }
    }
  }, [showRedirectMessage, redirectFromStorage, redirectParam, location.search, location.pathname]);
  const [courses, setCourses] = useState([]);
  const [additionalProducts, setAdditionalProducts] = useState([]);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [userEnrollments, setUserEnrollments] = useState([]);
  const [bulkPricing, setBulkPricing] = useState(null);
  const [expandedDescriptions, setExpandedDescriptions] = useState({});

  // Fetch user enrollments
  const fetchUserEnrollments = useCallback(async () => {
    if (!isAuthenticated) return;
    
    try {
      const response = await api.getUserEnrollments();
      setUserEnrollments(response.enrollments || []);
    } catch (error) {
      logger.error('Error fetching user enrollments', { error });
      // Don't set error state for enrollments, just log it
    }
  }, [isAuthenticated]);

  // Fetch courses and additional products
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch courses (now includes pricing information)
        const coursesResponse = await api.getPublicCourses();
        const coursesData = coursesResponse.courses || [];

        // Ensure all courses have pricing fields with defaults
        const coursesWithPricing = coursesData.map((course) => ({
          ...course,
          price: course.price ?? 0,
          currency: course.currency || 'USD',
          isPaidCourse: course.isPaidCourse ?? false
        }));

        // Fetch additional products
        const productsResponse = await api.getActiveAdditionalProducts();
        const productsData = productsResponse.products || [];

        setCourses(coursesWithPricing);
        setAdditionalProducts(productsData);
        setFilteredCourses(coursesWithPricing);
        setFilteredProducts(productsData);
        
        // Fetch user enrollments if authenticated
        if (isAuthenticated) {
          await fetchUserEnrollments();
        }
      } catch (error) {
        logger.error('Error fetching data', { error });
        setError('Failed to load courses and products');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isAuthenticated, user, fetchUserEnrollments]);

  // Filter courses and products based on search term and price filter
  useEffect(() => {
    let filteredCourses = courses;
    let filteredProducts = additionalProducts;

    // Filter by search term
    if (searchTerm) {
      filteredCourses = filteredCourses.filter(course =>
        course.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        course.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        course.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      
      filteredProducts = filteredProducts.filter(product =>
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }


    setFilteredCourses(filteredCourses);
    setFilteredProducts(filteredProducts);
  }, [courses, additionalProducts, searchTerm]);


  const handleMultiEnroll = async () => {
    // Pre-fetch CSRF token before navigating to prevent "CSRF token is invalid or expired" errors
    try {
      await api.fetchCSRFToken();
    } catch (error) {
      logger.warn('Failed to pre-fetch CSRF token before checkout', { error });
      // Continue with navigation anyway - PaymentForm will try to fetch it again
    }
    
    // If only courses are selected, go directly to payment form
    if (selectedCourses.length > 0 && selectedProducts.length === 0) {
      const courseCodes = selectedCourses.map(course => course.code);
      navigate(`/enroll/multiple/${courseCodes.join(',')}`);
      return;
    }
    
    // If only products are selected, go directly to payment form
    if (selectedProducts.length > 0 && selectedCourses.length === 0) {
      const productIds = selectedProducts.map(product => product.id);
      navigate(`/purchase/products/${productIds.join(',')}`);
      return;
    }
    
    // If both courses and products are selected, go to mixed purchase flow
    if (selectedCourses.length > 0 && selectedProducts.length > 0) {
      const courseCodes = selectedCourses.map(course => course.code);
      const productIds = selectedProducts.map(product => product.id);
      navigate(`/enroll/mixed/${courseCodes.join(',')}/${productIds.join(',')}`);
      return;
    }
  };

  const toggleCourseSelection = (course) => {
    setSelectedCourses(prev => {
      const isSelected = prev.some(selected => selected.id === course.id);
      if (isSelected) {
        return prev.filter(selected => selected.id !== course.id);
      } else {
        return [...prev, course];
      }
    });
  };

  const toggleProductSelection = (product) => {
    setSelectedProducts(prev => {
      const isSelected = prev.some(selected => selected.id === product.id);
      if (isSelected) {
        return prev.filter(selected => selected.id !== product.id);
      } else {
        return [...prev, product];
      }
    });
  };

  // Calculate bulk pricing when courses are selected
  useEffect(() => {
    const calculateBulkPricing = async () => {
      // Only calculate bulk pricing if 2+ courses are selected and user is authenticated
      if (selectedCourses.length >= 2 && isAuthenticated) {
        try {
          const courseIds = selectedCourses
            .filter(course => course.isPaidCourse)
            .map(course => parseInt(course.id));
          
          if (courseIds.length >= 2) {
            const response = await api.calculateBulkPricing(courseIds);
            if (response.success && response.bulkPricing) {
              setBulkPricing(response);
            } else {
              setBulkPricing(null);
            }
          } else {
            setBulkPricing(null);
          }
        } catch (error) {
          logger.error('Error calculating bulk pricing', { error });
          setBulkPricing(null);
        }
      } else {
        setBulkPricing(null);
      }
    };

    calculateBulkPricing();
  }, [selectedCourses, isAuthenticated]);

  // Check if user is enrolled in a course
  const isEnrolledInCourse = (courseId) => {
    return userEnrollments.some(enrollment => enrollment.course_id === courseId);
  };


  const getTotalPrice = () => {
    const courseTotal = selectedCourses.reduce((total, course) => {
      return total + (course.isPaidCourse ? parseFloat(course.price) : 0);
    }, 0);
    
    const productTotal = selectedProducts.reduce((total, product) => {
      return total + parseFloat(product.price || 0);
    }, 0);
    
    const subtotal = courseTotal + productTotal;
    
    // Apply bulk pricing discount if available
    if (bulkPricing && bulkPricing.pricing) {
      // Only apply bulk pricing discount to courses, not products
      const courseDiscount = bulkPricing.pricing.discountAmount || 0;
      return Math.max(0, subtotal - courseDiscount);
    }
    
    return subtotal;
  };

  const getOriginalTotal = () => {
    const courseTotal = selectedCourses.reduce((total, course) => {
      return total + (course.isPaidCourse ? parseFloat(course.price) : 0);
    }, 0);
    
    const productTotal = selectedProducts.reduce((total, product) => {
      return total + parseFloat(product.price || 0);
    }, 0);
    
    return courseTotal + productTotal;
  };

  // Course card component with description expansion
  const CourseCard = ({ course }) => {
    const descRef = useRef(null);
    const [isTruncated, setIsTruncated] = useState(false);
    const isSelected = selectedCourses.some(selected => selected.id === course.id);
    const isExpanded = expandedDescriptions[`course-${course.id}`];

    useEffect(() => {
      const checkTruncation = () => {
        if (descRef.current) {
          // Use requestAnimationFrame to ensure layout is complete
          requestAnimationFrame(() => {
            if (descRef.current) {
              const element = descRef.current;
              // Check if content is truncated (add 1px tolerance)
              const isTrunc = element.scrollHeight > element.clientHeight + 1;
              setIsTruncated(isTrunc);
            }
          });
        }
      };

      checkTruncation();

      // Also check after fonts load
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(checkTruncation);
      }
    }, [course.description]);

    const toggleExpansion = () => {
      setExpandedDescriptions(prev => ({
        ...prev,
        [`course-${course.id}`]: !prev[`course-${course.id}`]
      }));
    };

    const handleCardClick = (e) => {
      // Don't navigate if clicking on interactive elements
      const target = e.target;
      if (
        target.closest('button') || 
        target.closest('a') ||
        target.closest(`.${styles.readMoreButton}`) ||
        target.closest(`.${styles.ctaButton}`)
      ) {
        return;
      }
      
      // Navigate to course detail page
      navigate(`/courses/${course.id}`);
    };

    return (
      <motion.div
        className={`${styles.catalogCard} ${isSelected ? styles.selected : ''}`}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        onClick={handleCardClick}
        style={{ cursor: 'pointer' }}
      >
        <div className={styles.cardHeader}>
          {!isEnrolledInCourse(course.id) && !course.isPaidCourse && (
            <span className={styles.freeBadge}>Free</span>
          )}
          <div className={styles.courseCode}>
            {course.code}
          </div>
          <h3 className={styles.cardTitle}>
            {course.name}
          </h3>
          <div className={styles.descriptionWrapper}>
            <p 
              ref={descRef}
              className={`${styles.cardDescription} ${isExpanded ? styles.expanded : ''}`}
            >
              {course.description || 'No description available.'}
            </p>
            {isTruncated && (
              <button 
                className={styles.readMoreButton}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleExpansion();
                }}
                type="button"
              >
                {isExpanded ? (
                  <>
                    <FiChevronUp size={14} />
                    Show less
                  </>
                ) : (
                  <>
                    <FiChevronDown size={14} />
                    Read more
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        <div className={styles.cardMeta}>
          <div className={styles.metaItem}>
            <FiUser size={14} />
            <span>{course.instructor || 'TBA'}</span>
          </div>
          <div className={styles.metaItem}>
            <FiCalendar size={14} />
            <span>{course.academicYear || course.academic_year || '2025-2026'}</span>
          </div>
          <div className={styles.metaItem}>
            <FiBookOpen size={14} />
            <span>{formatDate(course.startDate || course.start_date)} - {formatDate(course.endDate || course.end_date)}</span>
          </div>
          <div className={styles.metaItem}>
            <FiUsers size={14} />
            <span>{course.maxStudents || course.max_students ? `${course.maxStudents || course.max_students} students max` : 'Unlimited'}</span>
          </div>
        </div>

        <div className={styles.cardFooter}>
          <div className={styles.priceInfo}>
            <span className={styles.priceLabel}>
              {course.isPaidCourse ? 'Price' : 'Free Course'}
            </span>
            <span className={styles.price}>
              {course.isPaidCourse ? `$${course.price ? parseFloat(course.price).toFixed(2) : '0.00'}` : 'Free'}
            </span>
          </div>
          {isEnrolledInCourse(course.id) ? (
            <div className={styles.enrolledBadge}>ALREADY ENROLLED</div>
          ) : (
            <button
              className={`${styles.ctaButton} ${isSelected ? styles.selected : ''}`}
              disabled={isEnrolledInCourse(course.id)}
              onClick={(e) => {
                e.stopPropagation();
                toggleCourseSelection(course);
              }}
            >
              {isSelected ? (
                <>
                  <FiMinus size={16} />
                  <span className={styles.buttonText}>Remove</span>
                </>
              ) : (
                <>
                  <FiPlus size={16} />
                  <span className={styles.buttonText}>Add to Cart</span>
                </>
              )}
            </button>
          )}
        </div>
      </motion.div>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'TBD';
    // Parse date-only strings without timezone conversion
    // Split the date string and create a local date object
    const [year, month, day] = dateString.split('T')[0].split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    return date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.errorMessage}>{error}</div>
      </div>
    );
  }

  const hasSelectedItems = selectedCourses.length > 0 || selectedProducts.length > 0;

  return (
    <>
      <div className={`${styles.container} ${hasSelectedItems ? styles.hasSelectedItems : ''}`}>
        <div className={styles.contentContainer}>
          {!showRedirectMessage && (
            <div className={styles.pageHeader}>
              <div className={styles.headerContent}>
                <div className={styles.headerText}>
                  <h1 className={styles.catalogTitle}>Course Catalog</h1>
                  <p className={styles.catalogSubtitle}>
                    Learn the essentials of Aqeedah and Fiqh through structured courses that support your spiritual growth.
                  </p>
                </div>
                <div className={styles.searchContainer}>
                  <FiSearch className={styles.searchIcon} size={20} />
                  <input
                    className={styles.searchInput}
                    type="text"
                    placeholder="Search catalog"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
            </div>
          )}

      {showRedirectMessage && (
        <motion.div
          className={styles.redirectMessage}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className={styles.messageIcon}>
            <FiInfo size={24} />
          </div>
          <p className={styles.messageText}>
            Choose your courses and proceed to checkout to complete your registration.
          </p>
        </motion.div>
      )}

      <motion.div
        className={styles.offerBanner}
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
      >
        <FiInfo className={styles.bannerIcon} size={18} />
        <span className={styles.bannerText}>
          Special offer: Enroll in 2 courses and get <strong>$20 off</strong> (active courses only)
        </span>
      </motion.div>

      {filteredCourses.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>
            <FiBookOpen />
          </div>
          <h3>No courses found</h3>
          <p>Try adjusting your search or filter criteria</p>
        </div>
      ) : (
        <div className={styles.catalogGrid}>
          {filteredCourses.map((course) => (
            <CourseCard key={course.id} course={course} />
          ))}
        </div>
      )}

      {/* Additional Products Section */}
      {filteredProducts.length > 0 && (
        <>
          <div className={styles.catalogGrid} style={{ marginTop: '3rem' }}>
            {filteredProducts.map((product) => {
              const isSelected = selectedProducts.some(selected => selected.id === product.id);
              return (
                <motion.div
                  key={product.id}
                  className={`${styles.catalogCard} ${isSelected ? styles.selected : ''}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className={styles.cardHeader}>
                    <h3 className={styles.cardTitle}>
                      {product.product_type === 'merchandise' ? (
                        <FiShoppingCart size={16} />
                      ) : product.product_type === 'curriculum_package' ? (
                        <FiBookOpen size={16} />
                      ) : (
                        <FiShoppingCart size={16} />
                      )}
                      {product.name}
                      {product.product_type === 'curriculum_package' && <span className={styles.featuredBadge}>Featured</span>}
                    </h3>
                    <p className={styles.cardDescription}>
                      {product.description || 'No description available.'}
                    </p>
                  </div>

                  <div className={styles.cardMeta}>
                    {/* Maintains card height consistency */}
                  </div>

                  <div className={styles.cardFooter}>
                    <div className={styles.priceInfo}>
                      <span className={styles.priceLabel}>Price</span>
                      <span className={styles.price}>
                        {`$${product.price ? parseFloat(product.price).toFixed(2) : '0.00'}`}
                      </span>
                    </div>
                    <button
                      className={`${styles.ctaButton} ${isSelected ? styles.selected : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleProductSelection(product);
                      }}
                    >
                      {isSelected ? (
                        <>
                          <FiMinus size={16} />
                          Remove from Cart
                        </>
                      ) : (
                        <>
                          <FiPlus size={16} />
                          Add to Cart
                        </>
                      )}
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </>
      )}
        </div>
      </div>

      {/* Sticky Checkout Bar - Only show when items are selected */}
      {hasSelectedItems && (
        <div className={styles.multiSelectControls}>
          <div className={styles.cartSummary}>
            <div className={styles.cartInfo}>
              <div className={styles.cartCount}>
                {selectedCourses.length + selectedProducts.length} item{(selectedCourses.length + selectedProducts.length) !== 1 ? 's' : ''} selected
                {selectedCourses.length > 0 && ` (${selectedCourses.length} course${selectedCourses.length !== 1 ? 's' : ''})`}
                {selectedProducts.length > 0 && ` (${selectedProducts.length} product${selectedProducts.length !== 1 ? 's' : ''})`}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.25rem' }}>
                {bulkPricing && bulkPricing.pricing && bulkPricing.pricing.discountAmount > 0 && (
                  <>
                    <span className={styles.cartOriginalTotal}>${getOriginalTotal().toFixed(2)}</span>
                    <span className={styles.cartDiscount}>
                      (Save ${bulkPricing.pricing.discountAmount.toFixed(2)})
                    </span>
                  </>
                )}
                <span className={styles.cartTotal}>Total: ${getTotalPrice().toFixed(2)}</span>
              </div>
            </div>
            <button 
              className={styles.enrollAllButton}
              onClick={handleMultiEnroll}
              disabled={selectedCourses.length === 0 && selectedProducts.length === 0}
            >
              <FiShoppingCart size={16} />
              Checkout
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default CourseCatalog;
